import { useEffect, useState } from 'react';
import AstroTipContent from '@/models/astro-tip-content';
import { useAstroTip, useCreateAstroTip, useUpdateAstroTip, useDeleteAstroTip, AstroTipMutationPayload } from '@/services/astro-tips';
import FormModal from '@/shared/components/form-modal';
import { MaterialFormContainer, MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { addButtonStyle, dangerButtonStyle, errorBannerStyle } from '@/shared/dark-inline-styles';

interface AstroTipModalProps {
    isOpen: boolean;
    selectedAstroTipId?: string;
    toggle: () => void;
}

// The 22 day-words and 4 elements the generator keys reading copy on.
const DAY_WORDS = [
    'Begin',
    'Charge',
    'Map',
    'Arrive',
    'Wait',
    'Steady',
    'Tune',
    'Rest',
    'Resist',
    'Release',
    'Allow',
    'Channel',
    'Befriend',
    'Author',
    'Choose',
    'Restore',
    'Stoke',
    'Flow',
    'Shield',
    'Own',
    'Forge',
    'Become',
];
const ELEMENTS = ['fire', 'earth', 'air', 'water'] as const;

// A repeater row keyed by a day-word. `headlines` rows carry multi-line text
// (one headline per line); `wordLine` rows carry a single line.
interface HeadlineRow {
    dayWord: string;
    lines: string;
}
interface WordLineRow {
    dayWord: string;
    line: string;
}

interface AstroTipForm {
    name: string;
    active: boolean;
    kicker: string;
    headlineRows: HeadlineRow[];
    wordLineRows: WordLineRow[];
    elementBody: Record<(typeof ELEMENTS)[number], string>;
    dos: string;
    donts: string;
}

const emptyElementBody = (): Record<(typeof ELEMENTS)[number], string> => ({
    fire: '',
    earth: '',
    air: '',
    water: '',
});

const emptyForm = (): AstroTipForm => ({
    name: '',
    active: true,
    kicker: '',
    headlineRows: [],
    wordLineRows: [],
    elementBody: emptyElementBody(),
    dos: '',
    donts: '',
});

const Hint = ({ children }: { children: React.ReactNode }) => (
    <p style={{ margin: '-6px 0 10px', fontSize: 12, color: '#999999', lineHeight: 1.5 }}>{children}</p>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => <p style={{ margin: '18px 0 4px', fontWeight: 600 }}>{children}</p>;

// Split a multi-line textarea into trimmed, non-empty lines.
const linesToArray = (text: string): string[] =>
    text
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);

const AstroTipModal = ({ isOpen, toggle, selectedAstroTipId }: AstroTipModalProps) => {
    const { data: astroTip, isLoading } = useAstroTip(selectedAstroTipId as string, isOpen);
    const { mutateAsync: createAstroTip, isPending: isCreating } = useCreateAstroTip();
    const { mutateAsync: updateAstroTip, isPending: isUpdating } = useUpdateAstroTip();
    const { mutateAsync: deleteAstroTip, isPending: isDeleting } = useDeleteAstroTip();

    const [form, setForm] = useState<AstroTipForm>(emptyForm());
    const [error, setError] = useState<string | null>(null);

    // `isOpen` in the deps so re-opening the SAME pool re-fills the form from the
    // cached data (the close-reset below would otherwise leave it blank).
    useEffect(() => {
        if (!isOpen) return;
        if (astroTip?.data) {
            const a: AstroTipContent = astroTip.data;
            const headlineRows: HeadlineRow[] = Object.entries(a.headlines ?? {}).map(([dayWord, lines]) => ({
                dayWord,
                lines: (lines ?? []).join('\n'),
            }));
            const wordLineRows: WordLineRow[] = Object.entries(a.wordLine ?? {}).map(([dayWord, line]) => ({
                dayWord,
                line: line ?? '',
            }));
            const elementBody = emptyElementBody();
            for (const el of ELEMENTS) {
                elementBody[el] = a.elementBody?.[el] ?? '';
            }
            setForm({
                name: a.name,
                active: a.active !== false,
                kicker: a.kicker ?? '',
                headlineRows,
                wordLineRows,
                elementBody,
                dos: (a.dos ?? []).join('\n'),
                donts: (a.donts ?? []).join('\n'),
            });
        } else if (!selectedAstroTipId) {
            setForm(emptyForm());
        }
        setError(null);
    }, [isOpen, astroTip?.data, selectedAstroTipId]);

    useEffect(() => {
        if (!isOpen) {
            setForm(emptyForm());
            setError(null);
        }
    }, [isOpen]);

    const set = <K extends keyof AstroTipForm>(key: K, value: AstroTipForm[K]) => setForm((f) => ({ ...f, [key]: value }));

    // ── Headlines repeater helpers ─────────────────────────────────────────
    const addHeadlineRow = () => setForm((f) => ({ ...f, headlineRows: [...f.headlineRows, { dayWord: '', lines: '' }] }));
    const removeHeadlineRow = (index: number) => setForm((f) => ({ ...f, headlineRows: f.headlineRows.filter((_, i) => i !== index) }));
    const setHeadlineRow = (index: number, patch: Partial<HeadlineRow>) =>
        setForm((f) => ({ ...f, headlineRows: f.headlineRows.map((r, i) => (i === index ? { ...r, ...patch } : r)) }));

    // ── Word-line repeater helpers ─────────────────────────────────────────
    const addWordLineRow = () => setForm((f) => ({ ...f, wordLineRows: [...f.wordLineRows, { dayWord: '', line: '' }] }));
    const removeWordLineRow = (index: number) => setForm((f) => ({ ...f, wordLineRows: f.wordLineRows.filter((_, i) => i !== index) }));
    const setWordLineRow = (index: number, patch: Partial<WordLineRow>) =>
        setForm((f) => ({ ...f, wordLineRows: f.wordLineRows.map((r, i) => (i === index ? { ...r, ...patch } : r)) }));

    const setElementBody = (el: (typeof ELEMENTS)[number], value: string) =>
        setForm((f) => ({ ...f, elementBody: { ...f.elementBody, [el]: value } }));

    const validate = (): string | null => {
        if (!form.name.trim()) return 'Name is required';
        return null;
    };

    const buildPayload = (): AstroTipMutationPayload => {
        // Headlines: each non-empty (day-word set + at least one line) row → array.
        const headlines: { [dayWord: string]: string[] } = {};
        for (const row of form.headlineRows) {
            const dayWord = row.dayWord.trim();
            if (!dayWord) continue;
            const lines = linesToArray(row.lines);
            if (lines.length === 0) continue;
            headlines[dayWord] = lines;
        }

        // Word line: each non-empty (day-word set + line) row → record.
        const wordLine: { [dayWord: string]: string } = {};
        for (const row of form.wordLineRows) {
            const dayWord = row.dayWord.trim();
            const line = row.line.trim();
            if (!dayWord || !line) continue;
            wordLine[dayWord] = line;
        }

        // Element body: only non-empty elements.
        const elementBody: { [element: string]: string } = {};
        for (const el of ELEMENTS) {
            const body = form.elementBody[el].trim();
            if (body) elementBody[el] = body;
        }

        const payload: AstroTipMutationPayload = {
            name: form.name.trim(),
            active: form.active,
            headlines,
            dos: linesToArray(form.dos),
            donts: linesToArray(form.donts),
        };

        // Omit empty optionals — the API validate() rejects unmatched body keys,
        // but these are declared optional so omitting them is correct.
        if (form.kicker.trim()) payload.kicker = form.kicker.trim();
        if (Object.keys(wordLine).length > 0) payload.wordLine = wordLine;
        if (Object.keys(elementBody).length > 0) payload.elementBody = elementBody;

        return payload;
    };

    const handleSave = async () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        try {
            const payload = buildPayload();
            // callApi never throws on API errors — inspect result.status.
            const result = selectedAstroTipId ? await updateAstroTip({ id: selectedAstroTipId, ...payload }) : await createAstroTip(payload);
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to save the tip pool.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to save astro tip pool:', e);
            setError('Failed to save the tip pool. Check the console for details.');
        }
    };

    const handleDelete = async () => {
        if (!selectedAstroTipId) return;
        try {
            const result = await deleteAstroTip({ id: selectedAstroTipId });
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to delete the tip pool.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to delete astro tip pool:', e);
            setError('Failed to delete the tip pool. Check the console for details.');
        }
    };

    return (
        <FormModal
            isOpen={isOpen}
            showDelete={!!selectedAstroTipId}
            title={`${selectedAstroTipId ? 'Edit' : 'New'} Tip Pool`}
            body={
                <MaterialFormContainer onSubmit={handleSave}>
                    {error && <div style={errorBannerStyle}>{error}</div>}

                    <MaterialTextField label="Name" value={form.name} onChange={(e) => set('name', e.target.value)} />
                    <Hint>An internal label for this copy pool. Not shown to users.</Hint>

                    <MaterialTextField
                        name="select"
                        select
                        label="Status"
                        value={form.active ? 'active' : 'inactive'}
                        onChange={(e) => set('active', e.target.value === 'active')}
                    >
                        <MaterialFormOption value="active">Active (overrides apply)</MaterialFormOption>
                        <MaterialFormOption value="inactive">Inactive (ignored)</MaterialFormOption>
                    </MaterialTextField>

                    <MaterialTextField label="Kicker (optional)" value={form.kicker} onChange={(e) => set('kicker', e.target.value)} />
                    <Hint>Overrides the built-in kicker line. Leave blank to keep the built-in copy.</Hint>

                    {/* ── Headlines ────────────────────────────────────────── */}
                    <SectionTitle>Headlines</SectionTitle>
                    <Hint>
                        Per day-word headline pool. One headline per line — the generator picks from the pool. Add a row per day-word you want to
                        override; leave the rest to fall back to the built-in copy.
                    </Hint>
                    {form.headlineRows.map((row, index) => (
                        <div key={index} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 8 }}>
                            <MaterialTextField
                                name="select"
                                select
                                label="Day-word"
                                style={{ flex: '0 0 180px' }}
                                value={row.dayWord}
                                onChange={(e) => setHeadlineRow(index, { dayWord: e.target.value })}
                            >
                                <MaterialFormOption value="">— Select —</MaterialFormOption>
                                {DAY_WORDS.map((w) => (
                                    <MaterialFormOption key={w} value={w}>
                                        {w}
                                    </MaterialFormOption>
                                ))}
                            </MaterialTextField>
                            <MaterialTextField
                                label="Headlines (one per line)"
                                multiline
                                value={row.lines}
                                onChange={(e) => setHeadlineRow(index, { lines: e.target.value })}
                            />
                            <button
                                type="button"
                                onClick={() => removeHeadlineRow(index)}
                                style={{ ...dangerButtonStyle, flex: '0 0 auto', marginTop: 16 }}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button type="button" onClick={addHeadlineRow} style={{ ...addButtonStyle, marginBottom: 8 }}>
                        + Add headline day-word
                    </button>

                    {/* ── Word line ────────────────────────────────────────── */}
                    <SectionTitle>Word line</SectionTitle>
                    <Hint>Per day-word one-line override. Add a row per day-word you want to override.</Hint>
                    {form.wordLineRows.map((row, index) => (
                        <div key={index} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 8 }}>
                            <MaterialTextField
                                name="select"
                                select
                                label="Day-word"
                                style={{ flex: '0 0 180px' }}
                                value={row.dayWord}
                                onChange={(e) => setWordLineRow(index, { dayWord: e.target.value })}
                            >
                                <MaterialFormOption value="">— Select —</MaterialFormOption>
                                {DAY_WORDS.map((w) => (
                                    <MaterialFormOption key={w} value={w}>
                                        {w}
                                    </MaterialFormOption>
                                ))}
                            </MaterialTextField>
                            <MaterialTextField label="Line" value={row.line} onChange={(e) => setWordLineRow(index, { line: e.target.value })} />
                            <button
                                type="button"
                                onClick={() => removeWordLineRow(index)}
                                style={{ ...dangerButtonStyle, flex: '0 0 auto', marginTop: 16 }}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button type="button" onClick={addWordLineRow} style={{ ...addButtonStyle, marginBottom: 8 }}>
                        + Add word-line day-word
                    </button>

                    {/* ── Element body ─────────────────────────────────────── */}
                    <SectionTitle>Element body</SectionTitle>
                    <Hint>Per element body copy. Leave an element blank to keep its built-in copy.</Hint>
                    {ELEMENTS.map((el) => (
                        <MaterialTextField
                            key={el}
                            label={`${el.charAt(0).toUpperCase() + el.slice(1)} body`}
                            multiline
                            value={form.elementBody[el]}
                            onChange={(e) => setElementBody(el, e.target.value)}
                        />
                    ))}

                    {/* ── Dos / Donts ──────────────────────────────────────── */}
                    <SectionTitle>Dos</SectionTitle>
                    <Hint>One item per line. Leave blank to keep the built-in list.</Hint>
                    <MaterialTextField label="Dos (one per line)" multiline value={form.dos} onChange={(e) => set('dos', e.target.value)} />

                    <SectionTitle>Donts</SectionTitle>
                    <Hint>One item per line. Leave blank to keep the built-in list.</Hint>
                    <MaterialTextField label="Donts (one per line)" multiline value={form.donts} onChange={(e) => set('donts', e.target.value)} />
                </MaterialFormContainer>
            }
            toggle={toggle}
            handleSave={handleSave}
            handleDelete={handleDelete}
            loading={isLoading || isCreating || isUpdating || isDeleting}
        />
    );
};

export default AstroTipModal;
